/* The /dbx-reader page.
 *
 * A .dbx is one folder, so the tool takes one file at a time and shows the mail
 * inside it. Folders.dbx is a different animal — it is the folder LIST, with no
 * messages at all — and saying so plainly is most of the value there: people
 * drop it expecting mail and conclude the tool is broken.
 *
 * No worker: these files are small by modern standards (the largest real one
 * here is 142 KB) and the parse is a tree walk plus a segment chain.
 */

import { inspect, messageBytes, previewText, toMbox, emlName, DBX_KIND } from './dbx-read.js'
import { reportShape } from '../file-shape.js'
import { suggestOtherTool } from '../file-signature.js'

const T = window.__DBX_I18N || {}
const t = (k, fallback, vars) => {
  let s = T[k] || fallback || k
  /* Longest placeholder first: ':n' is a prefix of ':name'. */
  if (vars) {
    for (const [n, v] of Object.entries(vars).sort((a, b) => b[0].length - a[0].length)) {
      s = s.split(':' + n).join(String(v))
    }
  }
  return s
}
const $ = s => document.querySelector(s)
const el = (tag, cls, text) => {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}

const state = { info: null, name: '', selected: -1, limit: null }

function setStatus(msg, kind = 'info') {
  const box = $('#dbx-status')
  if (!box) return
  box.hidden = !msg
  box.className = 'dbx-status lg-card dbx-status--' + kind
  box.textContent = msg || ''
}

const fmtBytes = n => {
  const u = [t('unit_b', 'B'), t('unit_kb', 'KB'), t('unit_mb', 'MB')]
  let i = 0
  let v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v < 10 && i ? v.toFixed(1) : Math.round(v)} ${u[i]}`
}
const fmtDate = d => (d ? d.toISOString().slice(0, 16).replace('T', ' ') : '—')

async function open(file) {
  state.name = file.name
  setStatus(t('opening', 'Reading the folder…'))
  $('#dbx-workspace').hidden = true
  $('#dbx-summary').hidden = true

  const bytes = new Uint8Array(await file.arrayBuffer())
  const info = inspect(bytes)
  state.info = info
  state.selected = -1

  if (info.kind === DBX_KIND.NOT_DBX) {
    /* Not recognised at all. Worth hearing about: the second signature is what
       tells a message folder from Folders.dbx, and a build we have not seen
       would land exactly here. */
    try {
      reportShape({ format: 'dbx', size: bytes.length, ok: false, reason: 'not-dbx' })
    } catch (_) {}
    setStatus(t('err_not_dbx', 'This is not an Outlook Express folder file.'), 'error')
    try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'dbx-reader', document.querySelector('#dbx-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
    return
  }

  setStatus('')
  renderSummary()
  /* Export buttons only where there is something to export. On a folder LIST —
     or an empty folder — they would sit there looking available and do nothing,
     which is the same defect as a button that lies about what it does. */
  const exportable = info.kind === DBX_KIND.MESSAGES && info.messages.some(m => m.bodyAt)
  const actions = document.querySelector('.dbx-actions')
  if (actions) actions.hidden = !exportable

  if (info.kind === DBX_KIND.FOLDERS) {
    renderFolders()
  } else {
    renderList()
    if (info.messages.length) select(0)
  }
  /* Which .dbx stores actually turn up. The tag meanings differ per file kind,
     and the only way to learn that a build of Outlook Express numbered them
     differently is to see a store where the index walks but the messages come
     back empty. */
  try {
    reportShape({
      format: 'dbx',
      version: info.kind,
      size: bytes.length,
      ok: true,
      flags: [].concat(
        (info.problems || []).map(p => p && (p.kind || p.code || p)),
        info.orphans ? ['orphans'] : [],
        info.messages.length && !info.messages.some(m => m.bodyAt) ? ['no-bodies'] : [],
      ),
      counts: {
        folders: info.folders ? info.folders.length : 0,
        messages: info.messages.length,
        deleted: info.messages.filter(m => m.deleted).length,
        orphans: info.orphans || 0,
        declared: info.declared || 0,
      },
    })
  } catch (_) { /* telemetry must never break the tool */ }

  try { window.__stage?.('complete') } catch (_) {}
  $('#dbx-workspace').hidden = false
  state.limit?.commit()
  window.__aeToolComplete?.({ kind: info.kind, messages: info.messages.length })
}

function renderSummary() {
  const box = $('#dbx-summary')
  const i = state.info
  box.innerHTML = ''
  box.hidden = false

  const chips = el('div', 'dbx-chips')
  const chip = (label, value, kind = '') => {
    if (value == null || value === '') return
    const c = el('span', 'dbx-chip' + (kind ? ' dbx-chip--' + kind : ''))
    c.append(el('b', null, label), document.createTextNode(' '), el('span', null, String(value)))
    chips.append(c)
  }
  chip(t('chip_kind', 'File'), i.kind === DBX_KIND.FOLDERS
    ? t('kind_folders', 'Folder list (Folders.dbx)')
    : i.kind === DBX_KIND.MESSAGES ? t('kind_messages', 'Mail folder') : t('kind_other', 'Other Outlook Express store'))
  chip(t('chip_size', 'Size'), fmtBytes(i.size))
  if (i.kind === DBX_KIND.FOLDERS) chip(t('chip_folders', 'Folders'), i.folders.length)
  else {
    chip(t('chip_messages', 'Messages'), i.messages.filter(m => !m.deleted).length)
    if (i.orphans) chip(t('chip_orphans', 'Found outside the index'), i.orphans, 'accent')
  }
  box.append(chips)

  const notes = el('ul', 'dbx-notes')
  const note = (s, key = false) => notes.append(el('li', key ? 'dbx-note--key' : null, s))

  if (i.kind === DBX_KIND.FOLDERS) {
    note(t('note_folders', 'This file is the folder LIST, not a folder: it holds no messages. The mail itself lives in the files named beside each folder below.'), true)
  } else if (i.kind === DBX_KIND.OTHER) {
    note(t('note_other', 'Outlook Express used this file for something other than stored mail, so there are no messages to show.'), true)
  }
  if (i.problems.includes('short-index')) {
    note(t('note_short', 'The header claims :n messages and the index lists fewer. The ones that could be found are below.', { n: i.declared }), true)
  }
  if (i.problems.includes('index')) {
    note(t('note_index', 'The message index could not be walked. Anything below was found by reading the file directly.'), true)
  }
  if (i.orphans) {
    note(t('note_orphans', 'Some message records are not listed in the folder index. In Outlook Express that is what a deleted message looks like until the folder is compacted — they are marked below rather than mixed in.'), true)
  }
  if (i.problems.includes('body-cycle')) {
    note(t('note_cycle', 'One message body referred back to itself. It was cut at that point instead of being followed forever.'))
  }
  if (notes.children.length) {
    box.append(el('h4', 'dbx-sub', t('notes_title', 'What the file says about itself')), notes)
  }
}

function renderFolders() {
  $('#dbx-panes').hidden = true
  const host = $('#dbx-folders')
  host.hidden = false
  host.innerHTML = ''
  const table = el('table', 'dbx-grid')
  const thead = el('thead')
  const hr = el('tr')
  for (const h of [t('col_folder', 'Folder'), t('col_file', 'File')]) hr.append(el('th', null, h))
  thead.append(hr)
  const tbody = el('tbody')
  for (const f of state.info.folders) {
    const tr = el('tr')
    tr.append(el('td', null, f.name))
    tr.append(el('td', 'dbx-muted', f.file || '—'))
    tbody.append(tr)
  }
  table.append(thead, tbody)
  host.append(table)
}

function renderList() {
  $('#dbx-folders').hidden = true
  $('#dbx-panes').hidden = false
  const host = $('#dbx-list')
  host.innerHTML = ''
  const msgs = state.info.messages
  if (!msgs.length) {
    host.append(el('p', 'dbx-muted', t('no_messages', 'This folder holds no messages.')))
    return
  }
  msgs.forEach((m, i) => {
    const row = el('button', 'dbx-row' + (m.deleted ? ' is-deleted' : ''))
    row.type = 'button'
    row.append(el('span', 'dbx-row-subj', m.subject || t('no_subject', '(no subject)')))
    row.append(el('span', 'dbx-row-from', m.fromAddr || m.fromName || '—'))
    row.append(el('span', 'dbx-row-date', fmtDate(m.sent || m.received)))
    row.addEventListener('click', () => select(i))
    host.append(row)
  })
}

function select(i) {
  state.selected = i
  for (const b of document.querySelectorAll('.dbx-row.is-on')) b.classList.remove('is-on')
  document.querySelectorAll('.dbx-row')[i]?.classList.add('is-on')
  const host = $('#dbx-view')
  host.innerHTML = ''
  const m = state.info.messages[i]
  if (!m) return
  const bytes = messageBytes(state.info, m)
  if (!bytes.length) {
    host.append(el('p', 'dbx-muted', t('no_body', 'The body of this message is not in the file.')))
    return
  }
  const head = el('div', 'dbx-head')
  head.append(el('div', 'dbx-head-subj', m.subject || t('no_subject', '(no subject)')))
  head.append(el('div', 'dbx-muted', `${m.fromName || ''} <${m.fromAddr || '—'}> · ${fmtDate(m.sent || m.received)}`))
  if (m.deleted) head.append(el('div', 'dbx-badge', t('badge_deleted', 'not listed in the index')))
  host.append(head)
  /* The raw source, not a rendered message: this is a reader, and rendering
     someone's twenty-year-old HTML mail would mean executing it. */
  host.append(el('pre', 'dbx-src', previewText(bytes, 20000)))
}

const stem = () => state.name.replace(/\.dbx$/i, '') || 'folder'

function exportEml() {
  const i = state.selected
  const m = state.info?.messages[i]
  if (!m) return
  const b = messageBytes(state.info, m)
  if (b.length) window.__downloadFile?.(new Blob([b], { type: 'message/rfc822' }), emlName(m, i))
}

function exportMbox() {
  const info = state.info
  if (!info?.messages?.length) return
  const buf = toMbox(info, info.messages)
  if (buf.length) window.__downloadFile?.(new Blob([buf], { type: 'application/mbox' }), stem() + '.mbox')
}

function wire() {
  const drop = document.getElementById('dbxDrop')
  drop?.addEventListener('tool-uploader:file', async ev => {
    const file = ev.detail?.files?.length ? ev.detail.files[0] : ev.detail?.file
    if (!file) return
    /* Funnel stages. Without them a tool can only report that it finished —
       never that someone arrived, picked a file and got nothing, which is the
       case worth knowing about because that person leaves without writing in. */
    try { window.__stage?.('file_chosen', { mb: Math.round((file.size || 0) / 1048576) }) } catch (_) {}
    state.limit = window.__toolLimit?.begin('dbx-reader', file.name + ':' + file.size) || null
    try {
      await open(file)
    } catch (err) {
      try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'dbx-reader', document.querySelector('#dbx-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
      setStatus(t('err_open', 'This file could not be read: ') + (err?.message || err), 'error')
      window.__handleError?.(err, 'dbx-reader')
    }
  })
  drop?.addEventListener('tool-uploader:clear', () => {
    state.info = null
    $('#dbx-workspace').hidden = true
    $('#dbx-summary').hidden = true
    setStatus('')
  })
  $('#dbx-export-eml')?.addEventListener('click', exportEml)
  $('#dbx-export-mbox')?.addEventListener('click', exportMbox)
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire)
else wire()
